import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ResetPasswordMethodEnum } from '@core/enums/auth';
import { IPasswordManagement } from '@core/interfaces/auth';
import { IData } from '@core/interfaces/data.interface';
import { IUser } from '@core/interfaces/user.Interface';
import { TokenManager } from '@core/services/tokens/token-manager';
import { endPoints } from '@core/utils/constants';
import { environment } from '@env/environment';
import { EMPTY, map, of, tap } from 'rxjs';
import { API_URL } from './tokens/api-url.token';
import { ISystemParam } from '@core/interfaces/auth/system-params.interface';
import { Router } from '@angular/router';
import { toFormData } from '@core/utils/functions';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _user: IUser | null = null;

  get user() {
    return this._user as IUser;
  }

  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    private http: HttpClient,
    private router: Router
  ) {}

  login(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.LOGIN}`;

    return this.http
      .post<
        IData<{
          tokenId: string;
          accessToken: string;
          refreshToken: string;
        }>
      >(url, model)
      .pipe(
        tap(({ data }) =>
          Object.entries(data)
            .filter(([key]) => /[T|t]oken/.test(key))
            .forEach(([key, token]) => TokenManager.set(token, key))
        )
      );
  }

  resetPassword(model: string) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.RESET_PASSWORD}`;

    return this.http.post<IData<IPasswordManagement>>(url, model);
  }

  updatePassword(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.UPDATE_PASSWORD}`;

    return this.http.put(url, model);
  }

  confirmUser(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.CONFIRM_USER}`;

    return this.http.post(url, model);
  }

  changePassword(model: FormData) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.AUTH}/${endPoints.CHANGE_PASSWORD}`;
    const headers = {
      [TokenManager.HEADER_TYPE]: `${TokenManager.HEADER_PREFIX} ${TokenManager.get(TokenManager.TOKEN_ID)}`,
    };

    return this.http.post(url, model, { headers });
  }

  resendPasswordCode(model: FormData) {
    const url = `${this.apiUrl}/internal/auth/actualizar-clave`;
    model.append('esRegenerarCodigoOTP', JSON.stringify(true));

    return this.http.put<
      IData<{
        numeroTelefonoEnmascarado?: string;
        claveUsuarioActualizado: boolean;
        tipoMedioReinicioClaveNuevo: ResetPasswordMethodEnum;
        tiposMedioReinicioClavePermitidos: ResetPasswordMethodEnum[];
        otpRegenerado: {
          solicitudGestionUsuarioId: number;
          tokenId: string;
        };
      }>
    >(url, model);
  }

  logout(force: boolean = true) {
    this._user = null;
    TokenManager.removeToken();
    force && this.router.navigate(['auth']);

    return EMPTY;
  }

  refreshToken() {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/auth/refresh-token`;
    const headers = new HttpHeaders()
      .append('x-api-key', environment.API_KEY as string)
      .append(
        'refresh-token',
        TokenManager.get(TokenManager.REFRESH_TOKEN) as string
      );

    return this.http
      .get<
        IData<{
          tokenId: string;
          accessToken: string;
        }>
      >(url, { headers })
      .pipe(
        tap(({ data }) =>
          Object.entries(data)
            .filter(([key]) => /[T|t]oken/.test(key))
            .forEach(([key, token]) => TokenManager.set(token as string, key))
        )
      );
  }

  resolve(opts?: { force: boolean }) {
    if (this._user && !opts?.force) {
      return of(this._user);
    }

    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.USER}/${endPoints.ME}`;

    return this.http.get<IData<IUser>>(url).pipe(
      map(x => x.data),
      tap(user => (this._user = user))
    );
  }

  getSystemParam() {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.PARAM}/${endPoints.SYSTEM}/SITE_KEY_RECAPTCHA/${endPoints.FIND}`;
    return this.http.get<IData<ISystemParam>>(url);
  }

  validateReCaptcha(model: { response: string }) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.RE_CAPTCHA}/${endPoints.VALIDATE}`;
    return this.http.post(url, model);
  }

  validateContactInit(model: any) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.USER}/${endPoints.CONTACT_MEDIA}/${endPoints.INITIALIZER}`;
    return this.http.post<
      IData<{
        solicitudGestionUsuarioId: number;
        tokenId: string;
      }>
    >(url, null, { params: model });
  }

  validateContact(model: any) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.USER}/${endPoints.CONTACT_MEDIA}/${endPoints.VERIFY}`;
    model.esConfirmarCodigoOTP = true;

    return this.http.post<
      IData<{
        otpValidado: boolean;
        otpRegenerado: {
          solicitudGestionUsuarioId: number;
          tokenId: string;
        };
      }>
    >(url, toFormData(model));
  }

  resendOTP(model: any) {
    const url = `${this.apiUrl}/${endPoints.INTERNAL}/${endPoints.USER}/${endPoints.CONTACT_MEDIA}/${endPoints.VERIFY}`;
    model.esRegenerarCodigoOTP = true;

    return this.http.post<
      IData<{
        otpValidado: boolean;
        otpRegenerado: {
          solicitudGestionUsuarioId: number;
          tokenId: string;
        };
      }>
    >(url, toFormData(model));
  }
}
